import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, keyframes } from "@mui/material";

// Define keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

function CuisinThree() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 } // Trigger when 30% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        textAlign: "center",
        padding: "40px 20px",
        maxWidth: "800px",
        margin: "0 auto",
        color: "#333",
      }}
    >
      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontSize: "36px",
          fontWeight: "bold",
          marginBottom: "16px",
          fontFamily: "Georgia, serif",
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `${fadeIn} 1s ease-in-out` : "none",
        }}
      >
        Culinary Experience: Learn the Art of Making Traditional Tamil Vade
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          lineHeight: 1.8,
          marginBottom: "32px",
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `${slideIn} 1s ease-in-out 0.5s` : "none",
          animationFillMode: "both",
        }}
      >
        Immerse yourself in the vibrant flavors of Tamil culture with our
        Vade-making experience. Guided by our talented local cook, you’ll
        uncover the secrets of preparing this iconic delicacy, celebrated for
        its crisp texture and savory taste.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          lineHeight: 1.8,
          marginBottom: "32px",
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `${slideIn} 1s ease-in-out 0.7s` : "none",
          animationFillMode: "both",
        }}
      >
        Using fresh, locally sourced ingredients, you’ll learn how to mix,
        shape, and fry the perfect Vade, while hearing stories and traditions
        that have kept this dish close to the hearts of Tamil families for
        generations.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          lineHeight: 1.8,
          marginBottom: "32px",
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `${slideIn} 1s ease-in-out 0.9s` : "none",
          animationFillMode: "both",
        }}
      >
        Whether you're a seasoned foodie or a curious beginner, this hands-on
        activity offers more than just cooking—it’s a celebration of culture and
        connection. And of course, the best part is savoring your freshly made
        Vade, paired with traditional chutneys or piping-hot tea.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          lineHeight: 1.8,
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `${slideIn} 1s ease-in-out 1.1s` : "none",
          animationFillMode: "both",
        }}
      >
        Join us in the kitchen and take home not just a recipe, but a piece of
        Tamil heritage to cherish forever.
      </Typography>
    </Box>
  );
}

export default CuisinThree;
